/* ========================================
   WEB VITALS MONITORING
   OPTIMIZED FOR PERFORMANCE TRACKING
   ======================================== */

import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

const isDev = process.env.NODE_ENV !== 'production';
const perfLogsEnabled = isDev && process.env.REACT_APP_ENABLE_PERF_LOGS === 'true';
const analyticsEndpoint = process.env.REACT_APP_ANALYTICS_URL;
const analyticsEndpointEnabled =
  process.env.REACT_APP_ENABLE_ANALYTICS_ENDPOINT === 'true' && !!analyticsEndpoint;

// Performance thresholds
const THRESHOLDS = {
  CLS: 0.1,
  FID: 100,
  FCP: 1800,
  LCP: 2500,
  TTFB: 800
};

// Performance categories
const getCategory = (metric, threshold) => {
  if (metric <= threshold) return 'good';
  if (metric <= threshold * 1.5) return 'needs-improvement';
  return 'poor';
};

// Send metrics to analytics (replace with your analytics service)
const sendToAnalytics = (metric) => {
  // Example: Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }

  // Optional custom analytics endpoint (disabled by default unless env vars are set)
  if (process.env.NODE_ENV === 'production' && analyticsEndpointEnabled) {
    fetch(analyticsEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        metric: metric.name,
        value: metric.value,
        category: getCategory(metric.value, THRESHOLDS[metric.name]),
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent
      })
    }).catch(() => {
      // Silently ignore analytics transport errors in production.
    });
  }
};

// Log metrics to console in development
const logMetric = (metric) => {
  if (!perfLogsEnabled) return;
  const category = getCategory(metric.value, THRESHOLDS[metric.name]);
  const emoji = category === 'good' ? '✅' : category === 'needs-improvement' ? '⚠️' : '❌';
  
  console.log(`${emoji} ${metric.name}: ${metric.value.toFixed(2)} (${category})`);
  
  if (category === 'poor') {
    console.warn(`Poor ${metric.name} detected. Consider optimization.`);
  }
};

// Initialize Web Vitals monitoring
export const initWebVitals = () => {
  // Core Web Vitals
  getCLS(logMetric);
  getFID(logMetric);
  getLCP(logMetric);

  // Additional metrics
  getFCP(logMetric);
  getTTFB(logMetric);

  // Send to analytics
  getCLS(sendToAnalytics);
  getFID(sendToAnalytics);
  getLCP(sendToAnalytics);
  getFCP(sendToAnalytics);
  getTTFB(sendToAnalytics);
};

// Performance observer for custom metrics
export const initPerformanceObserver = () => {
  if (!perfLogsEnabled) return;
  if ('PerformanceObserver' in window) {
    // Observe navigation timing
    const navigationObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.entryType === 'navigation') {
          const pageLoadValue =
            entry.loadEventEnd > 0 ? entry.loadEventEnd - entry.navigationStart : 0;
          const metrics = {
            'DNS Lookup': entry.domainLookupEnd - entry.domainLookupStart,
            'TCP Connection': entry.connectEnd - entry.connectStart,
            'TLS Negotiation': entry.secureConnectionStart > 0 ? entry.connectEnd - entry.secureConnectionStart : 0,
            'Request/Response': entry.responseEnd - entry.requestStart,
            'DOM Processing': entry.domContentLoadedEventEnd - entry.responseEnd,
            'Page Load': pageLoadValue
          };

          console.group('Navigation Timing');
          Object.entries(metrics).forEach(([name, value]) => {
            console.log(`${name}: ${value.toFixed(2)}ms`);
          });
          console.groupEnd();
        }
      });
    });

    try {
      navigationObserver.observe({ entryTypes: ['navigation'] });
    } catch (e) {
      console.warn('Navigation timing observation failed:', e);
    }

    // Observe resource timing
    const resourceObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const slowResources = entries.filter(entry => entry.duration > 1000);
      
      if (slowResources.length > 0) {
        console.warn('Slow resources detected:', slowResources.map(r => ({
          name: r.name,
          duration: `${r.duration.toFixed(2)}ms`,
          size: r.transferSize ? `${(r.transferSize / 1024).toFixed(2)}KB` : 'unknown'
        })));
      }
    });

    try {
      resourceObserver.observe({ entryTypes: ['resource'] });
    } catch (e) {
      console.warn('Resource timing observation failed:', e);
    }
  }
};

// Memory usage monitoring
export const initMemoryMonitoring = () => {
  if (!perfLogsEnabled) return;
  if ('memory' in performance) {
    const logMemoryUsage = () => {
      const memory = performance.memory;
      const usage = {
        used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
        total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)}MB`,
        limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)}MB`
      };

      console.log('Memory Usage:', usage);

      // Warn if memory usage is high
      const usagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
      if (usagePercent > 80) {
        console.warn(`High memory usage detected: ${usagePercent.toFixed(1)}%`);
      }
    };

    // Log memory usage every 30 seconds
    setInterval(logMemoryUsage, 30000);
    
    // Initial memory check
    setTimeout(logMemoryUsage, 5000);
  }
};

// Network information monitoring
export const initNetworkMonitoring = () => {
  if (!perfLogsEnabled) return;
  if ('connection' in navigator) {
    const connection = navigator.connection;
    const networkInfo = {
      effectiveType: connection.effectiveType,
      downlink: `${connection.downlink}Mbps`,
      rtt: `${connection.rtt}ms`,
      saveData: connection.saveData
    };

    console.log('Network Information:', networkInfo);

    // Adjust loading strategy based on connection
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      console.log('Slow connection detected. Optimizing for low bandwidth.');
      document.documentElement.classList.add('slow-connection');
    }

    if (connection.saveData) {
      console.log('Data saver mode detected. Reducing resource usage.');
      document.documentElement.classList.add('data-saver');
    }
  }
};

// Initialize all monitoring
export const initAllMonitoring = () => {
  if (!perfLogsEnabled && !analyticsEndpointEnabled) {
    return;
  }
  initWebVitals();
  initPerformanceObserver();
  initMemoryMonitoring();
  initNetworkMonitoring();
};

const webVitals = {
  initWebVitals,
  initPerformanceObserver,
  initMemoryMonitoring,
  initNetworkMonitoring,
  initAllMonitoring
};

export default webVitals;
